import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Play, Pause } from 'lucide-react';

export function ArxivAutoImportConfig() {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    id: '',
    enabled: false,
    papers_per_day: 10,
    delay_between_papers_minutes: 1,
    last_scheduled_at: null as string | null
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('arxiv_auto_import_config')
        .select('*')
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data) {
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const configData = {
        enabled: config.enabled,
        papers_per_day: config.papers_per_day,
        delay_between_papers_minutes: config.delay_between_papers_minutes,
        admin_user_id: user.id
      };

      if (config.id) {
        const { error } = await supabase
          .from('arxiv_auto_import_config')
          .update(configData)
          .eq('id', config.id);

        if (error) throw error;
        await fetchConfig();
      } else {
        const { error } = await supabase
          .from('arxiv_auto_import_config')
          .insert(configData)
          .select()
          .single();

        if (error) throw error;
        await fetchConfig();
      }

      toast.success('Configuration saved successfully');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const testScheduler = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('schedule-arxiv-imports', {
        body: { testMode: true }
      });
      if (error) throw error;
      
      toast.success('Scheduler executed successfully (Test Mode)');
      await fetchConfig();
    } catch (error) {
      console.error('Error testing scheduler:', error);
      toast.error('Failed to execute scheduler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Auto-Import Configuration</CardTitle>
        <CardDescription>
          Automatically import papers from arXiv cache to the platform using a daily cron job
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enabled">Enable Auto-Import</Label>
            <div className="text-sm text-muted-foreground">
              Run daily cron job to import papers
            </div>
          </div>
          <Switch
            id="enabled"
            checked={config.enabled}
            onCheckedChange={(enabled) => setConfig({ ...config, enabled })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="papers_per_day">Papers Per Day</Label>
          <Input
            id="papers_per_day"
            type="number"
            min="1"
            max="1000"
            value={config.papers_per_day}
            onChange={(e) => setConfig({ ...config, papers_per_day: parseInt(e.target.value) || 1 })}
          />
          <p className="text-sm text-muted-foreground">
            Number of papers to import each day
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="delay_minutes">Delay Between Papers (minutes)</Label>
          <Input
            id="delay_minutes"
            type="number"
            min="1"
            max="60"
            value={config.delay_between_papers_minutes}
            onChange={(e) => setConfig({ ...config, delay_between_papers_minutes: parseInt(e.target.value) || 1 })}
          />
          <p className="text-sm text-muted-foreground">
            Time delay between processing each paper
          </p>
        </div>

        {config.last_scheduled_at && (
          <div className="rounded-lg bg-muted p-4">
            <p className="text-sm">
              <span className="font-medium">Last Scheduled:</span>{' '}
              {new Date(config.last_scheduled_at).toLocaleString()}
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={saveConfig} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Configuration
          </Button>
          <Button onClick={testScheduler} variant="outline" disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
            Test Scheduler
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
